import React from "react";
import {Grid2 as Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import { AdminI18N, CommonI18N } from "@/translations";
import {CompanyRelationshipStatus, Country} from "@/api/data-contracts";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import SelectInputField from "@/lib/components/inputfields/select-input-field";

type Props = {
    name: string;
    status?: CompanyRelationshipStatus;
    country: Country | "ALL";
    onChange: (patch: Partial<{ name: string; status?: CompanyRelationshipStatus; country: Country | "ALL" }>) => void;
};

const CompanyFilterBar: React.FC<Props> = ({ name, status, country, onChange }) => {
    const { t } = useTranslation();

    const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value as CompanyRelationshipStatus | "ALL";
        if (value === "ALL") {
            onChange({ status: undefined });
        } else if (value === CompanyRelationshipStatus.WHITELIST) {
            onChange({ status: CompanyRelationshipStatus.WHITELIST });
        } else if (value === CompanyRelationshipStatus.BLACKLIST) {
            onChange({ status: CompanyRelationshipStatus.BLACKLIST });
        }
    };

    const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const v = e.target.value;
        const next = v === "ALL" ? "ALL" : (v as unknown as Country);
        onChange({ country: next });
    };

    const selectedCountryValue: string = country === "ALL" ? "ALL" : String(country);
    let selectedStatusValue: string = "ALL";
    if (status === CompanyRelationshipStatus.WHITELIST) {
        selectedStatusValue = CompanyRelationshipStatus.WHITELIST;
    } else if (status === CompanyRelationshipStatus.BLACKLIST) {
        selectedStatusValue = CompanyRelationshipStatus.BLACKLIST;
    }

    return (
        <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 3 }}>
                <StringInputField
                        label={t(AdminI18N.companyRelationships.filter.name)}
                    value={name}
                    onChange={(e) => onChange({ name: e.target.value })}
                />
            </Grid>
            <Grid size={{ xs: 1 }}>
                <SelectInputField
                    name="status"
                    label={t(AdminI18N.companyRelationships.filter.status)}
                    value={selectedStatusValue}
                    onChange={handleStatusChange}
                    options={[
                        {value: "ALL", label: t(AdminI18N.companyRelationships.status.all)},
                        {
                            value: CompanyRelationshipStatus.WHITELIST,
                            label: t(AdminI18N.companyRelationships.status.whitelist)
                        },
                        {
                            value: CompanyRelationshipStatus.BLACKLIST,
                            label: t(AdminI18N.companyRelationships.status.blacklist)
                        }
                    ]}
                />
            </Grid>
            <Grid size={{ xs: 2 }}>
                <SelectInputField
                    name="country"
                    label={t(AdminI18N.companyRelationships.filter.country)}
                    value={selectedCountryValue}
                    onChange={handleCountryChange}
                    options={[
                        {value: "ALL", label: t(AdminI18N.companyRelationships.filter.countryAll)},
                        ...(Object.keys(Country) as Array<keyof typeof Country>).map((key) => ({
                            value: String(Country[key]),
                            label: t(CommonI18N.enum.country[key])
                        }))
                    ]}
                />
            </Grid>
        </Grid>
    );
};

export default CompanyFilterBar;
